import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirebaseAuth } from '@/services/firebase';
import { useAuth } from '@/context';

// Default timeout values
const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const DEFAULT_WARNING_BEFORE_TIMEOUT_MS = 1 * 60 * 1000; // 1 minute warning
const DEFAULT_CHECK_INTERVAL_MS = 1000; // Check every second

// Activity events for different sensitivity levels
const ACTIVITY_EVENTS: Record<string, string[]> = {
  high: ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove', 'click', 'focus'],
  medium: ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'],
  low: ['keydown', 'click', 'touchstart']
};

interface SessionTimeoutOptions {
  timeoutMs?: number;
  warningMs?: number;
  redirectPath?: string;
  logoutMessage?: string;
  enabled?: boolean;
  sensitivity?: 'low' | 'medium' | 'high';
  checkInterval?: number;
  onWarning?: (timeSinceLastActivity: number) => void;
  onActivity?: (timestamp: number) => void;
  onLogout?: () => void | Promise<void>;
}

interface SessionTimeoutResult {
  showTimeoutWarning: boolean;
  secondsUntilTimeout: number;
  isActive: boolean;
  sessionStatus: 'active' | 'warning' | 'inactive';
  resetTimer: () => void;
  extendSession: () => void;
  logoutUser: () => Promise<void>;
}

/**
 * Optimized hook to manage user session timeouts with enhanced features
 */
export const useSessionTimeoutOptimized = (
  options: SessionTimeoutOptions = {}
): SessionTimeoutResult => {
  const {
    timeoutMs = DEFAULT_SESSION_TIMEOUT_MS,
    warningMs = DEFAULT_WARNING_BEFORE_TIMEOUT_MS,
    redirectPath = '/login',
    logoutMessage = 'You were logged out due to inactivity',
    enabled = true,
    sensitivity = 'medium',
    checkInterval = DEFAULT_CHECK_INTERVAL_MS,
    onWarning,
    onActivity,
    onLogout
  } = options;

  // Environment-aware configuration
  // In development, we want to avoid annoying timeouts while debugging
  // In production, we must enforce HIPAA compliance (typically 15-30 mins)
  // SOC2 L1: the dev override (24h) must NEVER apply to a production deployment.
  // Gate on the build-time DEV flag AND a hostname check, so even a stray dev
  // build served on a deployed host still enforces the real idle timeout.
  const isDeployedHost =
    typeof location !== 'undefined' &&
    /(?:chartly\.app|\.netlify\.app)$/i.test(location.hostname);
  const isDev = import.meta.env.DEV && !isDeployedHost;

  // For this implementation, we keep it enabled but with a very long timeout in dev
  // so that the mechanism is still tested, but doesn't interrupt workflow.
  const actualTimeoutMs = isDev ? (24 * 60 * 60 * 1000) : timeoutMs;

  if (isDev) {
    console.debug(`[SessionTimeout] Running in DEV mode. Timeout extended to ${actualTimeoutMs / 1000 / 60 / 60} hours.`);
  }

  const { user } = useAuth() as { user: { uid?: string } | null };

  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const [showTimeoutWarning, setShowTimeoutWarning] = useState<boolean>(false);
  const [secondsUntilTimeout, setSecondsUntilTimeout] = useState<number>(Math.floor(warningMs / 1000));
  const [isActive, setIsActive] = useState<boolean>(true);
  const [sessionStatus, setSessionStatus] = useState<'active' | 'warning' | 'inactive'>('active');

  const navigate = useNavigate();

  // Refs for cleanup and state management
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isMountedRef = useRef<boolean>(true);
  const warningCallbackFiredRef = useRef<boolean>(false);

  // Memoize activity events based on sensitivity
  const activityEvents = useMemo((): string[] => {
    return ACTIVITY_EVENTS[sensitivity] || ACTIVITY_EVENTS.medium;
  }, [sensitivity]);

  // Optimized activity reset with throttling
  const resetTimer = useCallback((): void => {
    const now = Date.now();

    // Throttle activity updates to prevent excessive state updates
    if (now - lastActivityRef.current < 1000) return;

    lastActivityRef.current = now;
    setLastActivity(now);
    setShowTimeoutWarning(false);
    setIsActive(true);
    setSessionStatus('active');
    warningCallbackFiredRef.current = false;

    // Call activity callback if provided
    if (onActivity && typeof onActivity === 'function') {
      try {
        onActivity(now);
      } catch (err) {
        console.error('Error in onActivity callback:', err);
      }
    }
  }, [onActivity]);

  // Extend session when user responds to warning
  const extendSession = useCallback((): void => {
    resetTimer();
    console.debug('Session extended by user action');
  }, [resetTimer]);

  // Enhanced logout function with proper cleanup
  const logoutUser = useCallback(async (): Promise<void> => {
    if (!isMountedRef.current) return;

    try {
      setSessionStatus('inactive');

      // Call logout callback if provided
      if (onLogout && typeof onLogout === 'function') {
        try {
          await onLogout();
        } catch (err) {
          console.error('Error in onLogout callback:', err);
        }
      }

      // Sign out from Firebase
      const auth = await getFirebaseAuth();
      const { signOut } = await import('firebase/auth');
      await signOut(auth);

      // Navigate to login page
      navigate(redirectPath, {
        state: { message: logoutMessage },
        replace: true
      });

      console.debug('User logged out due to session timeout');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still try to navigate even if signOut fails
      navigate(redirectPath, {
        state: { message: 'Logout error occurred' },
        replace: true
      });
    }
  }, [navigate, redirectPath, logoutMessage, onLogout]);

  // Enhanced activity checker with better state management
  const checkActivity = useCallback((): void => {
    if (!enabled || !user || !isMountedRef.current) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;

    // Show warning before timeout
    if (timeSinceLastActivity > actualTimeoutMs - warningMs) {
      if (!showTimeoutWarning) {
        setShowTimeoutWarning(true);
        setSessionStatus('warning');

        // Fire warning callback once
        if (!warningCallbackFiredRef.current && onWarning && typeof onWarning === 'function') {
          warningCallbackFiredRef.current = true;
          try {
            onWarning(timeSinceLastActivity);
          } catch (err) {
            console.error('Error in onWarning callback:', err);
          }
        }
      }

      // Calculate seconds until timeout
      const secondsLeft = Math.max(0, Math.floor((actualTimeoutMs - timeSinceLastActivity) / 1000));
      setSecondsUntilTimeout(secondsLeft);

      // Mark as inactive when very close to timeout
      if (secondsLeft < 5) {
        setIsActive(false);
      }
    }

    // Log out after timeout
    if (timeSinceLastActivity > actualTimeoutMs) {
      console.debug('Session timeout reached - logging out');
      logoutUser();
    }
  }, [enabled, lastActivity, actualTimeoutMs, warningMs, showTimeoutWarning, onWarning, logoutUser]);

  // Set up activity listeners and timeout checker
  useEffect(() => {
    if (!enabled || !user) {
      return;
    }

    // Add event listeners with passive option for better performance
    const addEventListeners = (): void => {
      activityEvents.forEach(event => {
        document.addEventListener(event, resetTimer, {
          passive: true,
          capture: false
        });
      });
    };

    // Remove event listeners
    const removeEventListeners = (): void => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetTimer, { capture: false });
      });
    };

    // Set up activity listeners
    addEventListeners();

    // Set up interval checker
    intervalRef.current = setInterval(checkActivity, checkInterval);

    // Cleanup function
    return () => {
      removeEventListeners();

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, activityEvents, resetTimer, checkActivity, checkInterval]);

  // Reset timer when user authentication state changes
  useEffect(() => {
    if (user && enabled) {
      resetTimer();
    }
  }, [user, enabled, resetTimer]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Return session timeout state and functions
  return {
    showTimeoutWarning,
    secondsUntilTimeout,
    isActive,
    sessionStatus,
    resetTimer,
    extendSession,
    logoutUser
  };
};

export default useSessionTimeoutOptimized;
